"use client";

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { ClientAuthProvider, useClientAuth } from '@/lib/client-auth-context';
import { getAccessibleProperties } from '@/lib/services/firestore';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

function LoginContent() {
  const router = useRouter();
  const { 
    user, 
    clientUser, 
    loading, 
    signingIn,
    error, 
    signIn,
    clearError,
  } = useClientAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formErrors, setFormErrors] = useState<{ email?: string; password?: string }>({});
  const [redirecting, setRedirecting] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    const redirectToFirstProperty = async () => {
      if (!loading && user && clientUser && !redirecting) {
        setRedirecting(true);
        try {
          // Get first accessible property
          const properties = await getAccessibleProperties(clientUser.id);
          if (properties.length > 0) {
            router.push(`/report/${properties[0].id}/dashboard`);
          } else {
            // No properties - show error or redirect to a "no access" page
            router.push('/report/no-access');
          }
        } catch (err) {
          console.error('Error getting properties:', err);
          router.push('/report/no-access');
        }
      }
    };
    redirectToFirstProperty();
  }, [user, clientUser, loading, router, redirecting]);

  // Clear error when form changes
  useEffect(() => {
    if (error) {
      clearError();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, password]);

  const validateForm = (): boolean => {
    const errors: { email?: string; password?: string } = {};
    
    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password.trim()) {
      errors.password = 'Password is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const success = await signIn(email, password);

    if (success) {
      // Redirect will be handled by the useEffect above
      setRedirecting(true);
    }
  };

  if (loading || redirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">
            {redirecting ? 'Redirecting to your dashboard...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-sm">
        {/* Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-6">
          {/* Title */}
          <div className="space-y-1 py-4 ">
            <h1 className="text-2xl font-semibold text-gray-900 text-center">AEO Tracker</h1>
            <p className="text-gray-500 text-sm text-center">
              Enter your credentials to access your reports
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-3 text-sm">
              {error}
            </div>
          )}

          {/* Sign In Form */}
          <form onSubmit={handleSignIn} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 text-sm font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder=""
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-10 bg-white border-gray-300 focus:border-primary focus:ring-primary"
                disabled={signingIn}
              />
              {formErrors.email && (
                <p className="text-sm text-red-500">{formErrors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700 text-sm font-medium">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-10 bg-white border-gray-300 focus:border-primary focus:ring-primary"
                disabled={signingIn}
              />
              {formErrors.password && (
                <p className="text-sm text-red-500">{formErrors.password}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={signingIn}
              className="w-full h-10 mt-2"
            >
              {signingIn ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <ClientAuthProvider>
      <React.Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      }>
        <LoginContent />
      </React.Suspense>
    </ClientAuthProvider>
  );
}
